import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import StickyActions from '../../components/feature/StickyActions';
import Button from '../../components/base/Button';

const Home: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "NextStep",
      "description": "Komplette Firmengründung in Dubai mit deutschem Rundum-Service",
      "url": import.meta.env.VITE_SITE_URL || "https://nextstep-uae.com",
      "telephone": "+971585375437",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Dubai",
        "addressCountry": "AE",
        "addressRegion": "Dubai"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "25.2048",
        "longitude": "55.2708"
      },
      "openingHours": "Mo-Fr 09:00-18:00",
      "serviceArea": {
        "@type": "Country",
        "name": "United Arab Emirates"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Firmengründungsservices",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Firmengründung Dubai",
              "description": "Komplette Abwicklung Ihrer Firmengründung in Dubai und den VAE"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Business Visa",
              "description": "Beantragung und Bearbeitung von Investor und Business Visa"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Geschäftskonto",
              "description": "Unterstützung bei der Eröffnung von Geschäftskonten"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Büroservice",
              "description": "Büroräume und virtuelle Adressen für Ihr Unternehmen"
            }
          }
        ]
      }
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Wie lange dauert eine Firmengründung in Dubai?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Eine Firmengründung in Dubai dauert in der Regel 2-4 Wochen, abhängig von der gewählten Rechtsform und dem Emirat."
          }
        },
        {
          "@type": "Question",
          "name": "Welche Visa-Arten gibt es für Unternehmer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Es gibt verschiedene Visa-Arten: Investor Visa, Employment Visa, Family Visa und das neue Golden Visa für langfristige Aufenthalte."
          }
        },
        {
          "@type": "Question",
          "name": "Ist eine deutschsprachige Betreuung verfügbar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, NextStep bietet vollständig deutschsprachige Betreuung durch unser Team vor Ort in Dubai."
          }
        },
        {
          "@type": "Question",
          "name": "Welche Kosten entstehen bei der Firmengründung?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Die Kosten variieren je nach Rechtsform und Freizone. Mainland-Firmen starten ab ca. 15.000 AED, Freizonenfirmen ab ca. 10.000 AED. Wir erstellen Ihnen eine transparente Kostenübersicht."
          }
        },
        {
          "@type": "Question",
          "name": "Brauche ich einen lokalen Partner?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In Freizonen nicht. Bei Mainland-Firmen ist seit 2021 100% ausländisches Eigentum möglich. Wir beraten Sie zur optimalen Struktur."
          }
        },
        {
          "@type": "Question",
          "name": "Welche Rechtsformen gibt es?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Die gängigsten sind: FZ-LLC (Freizone), Mainland LLC, Branch Office und Offshore Company. Jede hat spezifische Vor- und Nachteile."
          }
        },
        {
          "@type": "Question",
          "name": "Kann ich meine Familie mitnehmen?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, mit einem Investor Visa können Sie Family Visa für Ehepartner und Kinder beantragen. Wir unterstützen Sie bei allen Anträgen."
          }
        },
        {
          "@type": "Question",
          "name": "Wie hoch sind die Steuern?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In den VAE gibt es keine Einkommensteuer für Privatpersonen. Seit 2023 gilt eine Körperschaftsteuer von 9% auf Gewinne über 375.000 AED. Freezone-Unternehmen können bei qualifizierten Auslandsgeschäften weiterhin von einem 0%-Steuersatz profitieren."
          }
        },
        {
          "@type": "Question",
          "name": "Brauche ich ein Büro?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, für die Lizenz benötigen Sie eine Geschäftsadresse. Wir bieten flexible Lösungen von virtuellen Büros bis zu vollausgestatteten Räumen."
          }
        },
        {
          "@type": "Question",
          "name": "Welche Dokumente werden benötigt?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Reisepass, Businessplan, Referenzschreiben und je nach Tätigkeit weitere Nachweise. Wir erstellen Ihnen eine vollständige Checkliste."
          }
        },
        {
          "@type": "Question",
          "name": "Kann ich von Deutschland aus gründen?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, vieles läuft remote. Für finale Schritte (Emirates ID, Bankkonto) ist ein Besuch in Dubai nötig - meist 3-5 Tage."
          }
        },
        {
          "@type": "Question",
          "name": "Welche Freizone ist die beste?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Das hängt von Ihrer Branche ab. DMCC für Trading, Media City für Medien, IFZA für flexible Lösungen. Wir beraten Sie individuell."
          }
        }
      ]
    };

    const combinedSchema = [schemaData, faqSchema];

    const schemaElement = document.getElementById('schema-org-data');
    if (schemaElement) {
      schemaElement.textContent = JSON.stringify(combinedSchema);
    }
  }, []);

  const handleWhatsAppClick = () => {
    const phoneNumber = '+971585375437';
    const message = encodeURIComponent('Hallo NextStep, ich interessiere mich für eine Firmengründung in Dubai und bitte um weitere Informationen. Mein Name ist: [Name]');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Firmengründung',
      description: 'Komplette Abwicklung von der Lizenz bis zur Emirates ID - alles aus einer Hand.',
    },
    {
      icon: 'ri-passport-line',
      title: 'Investor Visa',
      description: 'Business Visa für Sie und Ihre Familie mit langfristiger Aufenthaltsgenehmigung.',
    },
    {
      icon: 'ri-bank-line',
      title: 'Geschäftskonto',
      description: 'Kontoeröffnung bei Top-Banken in Dubai für Ihr Unternehmen.',
    },
    {
      icon: 'ri-home-line',
      title: 'Büroservice',
      description: 'Von virtuellen Adressen bis zu vollausgestatteten Büroräumen.',
    },
  ];

  const benefits = [
    {
      icon: 'ri-map-pin-line',
      title: 'Vor Ort in Dubai',
      description: 'Unser Team ist direkt in Dubai und kennt alle lokalen Prozesse.',
    },
    {
      icon: 'ri-user-voice-line',
      title: '100% Deutschsprachig',
      description: 'Komplette Betreuung in deutscher Sprache von Anfang bis Ende.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Rundum-Service',
      description: 'Von der Beratung bis zur fertigen Firma - alles aus einer Hand.',
    },
    {
      icon: 'ri-time-line',
      title: 'Schnell & Effizient',
      description: 'Dank unserer Erfahrung und Kontakte besonders schnelle Abwicklung.',
    },
  ];

  const successStories = [
    {
      name: 'Lukas Bergmann',
      quote: 'NextStep hat mir den gesamten Gründungsprozess abgenommen. Nach nur 3 Wochen hatte ich meine Lizenz, mein Visum und konnte direkt loslegen. Die deutschsprachige Betreuung war Gold wert!'
    },
    {
      name: 'Amira Khalil',
      quote: 'Als Unternehmerin war mir wichtig, 100% Eigentum zu haben. Das Team hat mir die perfekte Freizone empfohlen und alles professionell abgewickelt. Heute führe ich erfolgreich meine Agentur in Dubai.'
    },
    {
      name: 'Felix Hartmann',
      quote: 'Ich war skeptisch, ob eine Firmengründung aus Deutschland heraus funktioniert. NextStep hat alle Zweifel beseitigt - transparente Kommunikation, klare Prozesse und nur ein kurzer Trip nach Dubai war nötig.'
    },
    {
      name: 'Priya Kapoor',
      quote: 'Die Unterstützung bei der Kontoeröffnung und dem Golden Visa war hervorragend. NextStep kennt alle Insider-Tipps und hat mir viel Zeit und Nerven gespart. Absolut empfehlenswert!'
    },
    {
      name: 'Maximilian Richter',
      quote: 'Von der ersten Beratung bis zur fertigen Firma - alles lief reibungslos. Besonders beeindruckt hat mich die schnelle Abwicklung und die Expertise des Teams vor Ort.'
    },
    {
      name: 'Yasmin Al-Farsi',
      quote: 'NextStep hat mir nicht nur bei der Gründung geholfen, sondern auch wertvolle Kontakte vermittelt. Die persönliche Betreuung und das Verständnis für meine Branche waren ausschlaggebend.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/a935201fea87f90d4d1d38cc0549cb6b.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-600 text-white inline-block px-6 py-2 rounded-full text-sm font-semibold mb-6">
              Komplette Firmengründung mit deutschem Rundum-Service – alles aus einer Hand
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Professionelle <span className="text-red-500">Firmengründung</span> in Dubai
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-200 font-semibold">
              Wir bringen dein Business auf Erfolgskurs
            </p>
            <p className="text-lg md:text-xl mb-12 text-gray-300 max-w-3xl mx-auto">
              Von der ersten Idee bis zur fertigen Firma in Dubai - deutschsprachig begleitet und vor Ort betreut. Lizenz, Visa, Konto und Büro aus einer Hand.
            </p>

            <div className="flex justify-center">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="w-full sm:w-auto"
              >
                <i className="ri-whatsapp-line mr-3 text-xl"></i>
                Jetzt kostenlose Beratung starten
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Unsere <strong>Leistungen</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alles für Ihre erfolgreiche Firmengründung in Dubai - komplett aus einer Hand.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${service.icon} text-2xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4 mb-12">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere <strong className="text-red-600">Partner</strong>
            </h2>
            <p className="text-lg text-gray-600">
              Vertrauen Sie auf unsere starken Partnerschaften
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="flex animate-scroll">
            {/* First set of logos */}
            <div className="flex items-center space-x-8 md:space-x-16 px-4 md:px-8">
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/8e66481e0acd360436b7903a5a405dd7.jpeg" 
                  alt="Partner Logo 1" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/15c3c1d55882129356969832c8486931.jpeg" 
                  alt="Partner Logo 2" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/e95a9d908e9e88752d0c4b02ff89d1f7.jpeg" 
                  alt="Partner Logo 3" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/2afc6fc4b133cdf1e1daedd1cd403583.jpeg" 
                  alt="Partner Logo 4" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>

            {/* Duplicate set for seamless loop */}
            <div className="flex items-center space-x-8 md:space-x-16 px-4 md:px-8">
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/8e66481e0acd360436b7903a5a405dd7.jpeg" 
                  alt="Partner Logo 1" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/15c3c1d55882129356969832c8486931.jpeg" 
                  alt="Partner Logo 2" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/e95a9d908e9e88752d0c4b02ff89d1f7.jpeg" 
                  alt="Partner Logo 3" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/2afc6fc4b133cdf1e1daedd1cd403583.jpeg" 
                  alt="Partner Logo 4" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>

            {/* Third set for extra smooth loop */}
            <div className="flex items-center space-x-8 md:space-x-16 px-4 md:px-8">
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/8e66481e0acd360436b7903a5a405dd7.jpeg" 
                  alt="Partner Logo 1" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/15c3c1d55882129356969832c8486931.jpeg" 
                  alt="Partner Logo 2" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/e95a9d908e9e88752d0c4b02ff89d1f7.jpeg" 
                  alt="Partner Logo 3" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-shrink-0 w-40 md:w-64 h-20 md:h-32 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/2afc6fc4b133cdf1e1daedd1cd403583.jpeg" 
                  alt="Partner Logo 4" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Dubai Section */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Warum <strong className="text-red-600">Dubai</strong> für Ihr Business?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dubai bietet einzigartige Vorteile für Unternehmer und wachsende Businesses.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <img
                src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/7a84bd9fe066539198dbef85dd323788.png"
                alt="Dubai Business District"
                className="rounded-lg shadow-lg object-cover w-full h-80"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-percent-line text-xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">0% Einkommensteuer</h3>
                  <p className="text-gray-600">Keine Einkommensteuer für Privatpersonen. Körperschaftsteuer ab 9% auf Gewinne über 375.000 AED. Freezone-Unternehmen können bei qualifizierten Auslandsgeschäften weiterhin von einem 0%-Steuersatz profitieren.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Strategische Lage</h3>
                  <p className="text-gray-600">Perfekte Brücke zwischen Europa, Asien und Afrika.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">100% Eigentum</h3>
                  <p className="text-gray-600">Kein lokaler Partner nötig - volle Kontrolle über Ihr Unternehmen.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-star-line text-xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Schnelle Gründung</h3>
                  <p className="text-gray-600">In 2-4 Wochen zur fertigen Firma mit allen Lizenzen.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section id="process" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              In <strong className="text-red-600">3 Schritten</strong> zur eigenen Firma
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unser bewährter Prozess macht Ihre Firmengründung einfach und stressfrei.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Step 1 */}
              <div className="text-center relative">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-chat-3-line text-xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Kostenlose Beratung</h3>
                  <p className="text-gray-600 leading-relaxed">
                    WhatsApp Beratung zur Analyse Ihrer Geschäftsidee. Wir entwickeln die optimale Firmenstrategie.
                  </p>
                </div>
                {/* Arrow for desktop */}
                <div className="hidden md:block absolute top-10 -right-4 z-0">
                  <i className="ri-arrow-right-line text-3xl text-red-300"></i>
                </div>
              </div>

              {/* Step 2 */}
              <div className="text-center relative">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-list-3-line text-xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Dokumentation</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Wir kümmern uns um alle Dokumente, Anträge und Behördengänge. Sie bleiben entspannt.
                  </p>
                </div>
                {/* Arrow for desktop */}
                <div className="hidden md:block absolute top-10 -right-4 z-0">
                  <i className="ri-arrow-right-line text-3xl text-red-300"></i>
                </div>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Fertige Firma</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Lizenz, Visa, Konto und Büro - alles fertig. Sie können direkt durchstarten.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center mt-12">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-3 text-xl"></i>
                Jetzt kostenlose Beratung starten
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section id="success-stories" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              <strong className="text-red-600">Erfolgsgeschichten</strong> unserer Kunden
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Über 200 zufriedene Unternehmer haben mit uns ihre Firma in Dubai gegründet.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {successStories.map((story, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{story.name}</h3>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex text-red-500 mb-3">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">
                    "{story.quote}"
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={handleWhatsAppClick}
              size="lg"
              className="whitespace-nowrap"
            >
              <i className="ri-whatsapp-line mr-3 text-xl"></i>
              Ihre Erfolgsgeschichte beginnt hier
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Warum <strong className="text-red-500">NextStep</strong>?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Unsere Vorteile machen den Unterschied für Ihren erfolgreichen Start in Dubai.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`} style={{ fontSize: '24px', lineHeight: '1' }}></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-300 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Expanded */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Häufig gestellte <strong>Fragen</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alle wichtigen Antworten zur Firmengründung in Dubai auf einen Blick.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq1" className="hover:text-red-600 transition-colors">
                    Wie lange dauert eine Firmengründung?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  In der Regel 2-4 Wochen, abhängig von der gewählten Rechtsform und dem Emirat.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq2" className="hover:text-red-600 transition-colors">
                    Welche Visa-Arten gibt es?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Investor Visa, Employment Visa, Family Visa und das neue Golden Visa für langfristige Aufenthalte.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq3" className="hover:text-red-600 transition-colors">
                    Ist deutschsprachige Betreuung verfügbar?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Ja, NextStep bietet vollständig deutschsprachige Betreuung durch unser Team vor Ort in Dubai.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq4" className="hover:text-red-600 transition-colors">
                    Welche Kosten entstehen?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Mainland ab ca. 15.000 AED, Freizonen ab ca. 10.000 AED. Wir erstellen eine transparente Kostenübersicht.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq5" className="hover:text-red-600 transition-colors">
                    Brauche ich einen lokalen Partner?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  In Freizonen nicht. Bei Mainland ist seit 2021 100% ausländisches Eigentum möglich.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq6" className="hover:text-red-600 transition-colors">
                    Welche Rechtsformen gibt es?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  FZ-LLC (Freizone), Mainland LLC, Branch Office und Offshore Company. Jede hat spezifische Vorteile.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq7" className="hover:text-red-600 transition-colors">
                    Kann ich meine Familie mitnehmen?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Ja, mit einem Investor Visa können Sie Family Visa für Ehepartner und Kinder beantragen.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq8" className="hover:text-red-600 transition-colors">
                    Wie hoch sind die Steuern?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  In den VAE gibt es keine Einkommensteuer für Privatpersonen. Seit 2023 gilt eine Körperschaftsteuer von 9% auf Gewinne über 375.000 AED. Freezone-Unternehmen können bei qualifizierten Auslandsgeschäften weiterhin von einem 0%-Steuersatz profitieren.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq9" className="hover:text-red-600 transition-colors">
                    Brauche ich ein Büro?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Ja, für die Lizenz benötigen Sie eine Geschäftsadresse. Wir bieten flexible Lösungen.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq10" className="hover:text-red-600 transition-colors">
                    Welche Dokumente werden benötigt?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Reisepass, Businessplan, Referenzschreiben und je nach Tätigkeit weitere Nachweise.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq11" className="hover:text-red-600 transition-colors">
                    Kann ich von Deutschland aus gründen?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Ja, vieles läuft remote. Für finale Schritte ist ein Besuch in Dubai nötig - meist 3-5 Tage.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  <a href="#faq12" className="hover:text-red-600 transition-colors">
                    Welche Freizone ist die beste?
                  </a>
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Das hängt von Ihrer Branche ab. DMCC für Trading, Media City für Medien, IFZA für flexible Lösungen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit für Ihre Firmengründung in Dubai?
          </h2>
          <p className="text-xl mb-12 max-w-3xl mx-auto">
            Starten Sie jetzt mit einer kostenlosen Beratung über WhatsApp. Wir beantworten alle Ihre Fragen.
          </p>

          <div className="flex justify-center">
            <Button
              onClick={handleWhatsAppClick}
              size="lg"
              className="!bg-white !text-red-600 hover:!bg-gray-50 hover:!text-red-700"
            >
              <i className="ri-whatsapp-line mr-3 text-xl"></i>
              Jetzt kostenlose Beratung starten
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/872a27432b8adaad402c2abfed07012d/f6440142cf3924a76ade8e6e073ea665.jpeg" 
                  alt="NextStep - Where Ambitions Become Companies" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-gray-300 mb-4">
                Where Ambitions Become Companies
              </p>
              <p className="text-gray-400">
                Ihr Partner für Firmengründungen in Dubai.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">
                <a href="#contact" className="hover:text-red-500 transition-colors">Kontakt</a>
              </h4>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-center">
                  <i className="ri-phone-line mr-3"></i>
                  <a href="tel:+971585375437" className="hover:text-red-500 transition-colors">
                    +971 58 537 5437
                  </a>
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-line mr-3"></i>
                  <a href="mailto:info@nextstep-uae.com" className="hover:text-red-500 transition-colors">
                    info@nextstep-uae.com
                  </a>
                </div>
                <div className="flex items-center">
                  <i className="ri-whatsapp-line mr-3"></i>
                  <button onClick={handleWhatsAppClick} className="hover:text-red-500 transition-colors cursor-pointer">
                    WhatsApp Chat
                  </button>
                </div>
                <div className="flex items-center">
                  <i className="ri-map-pin-line mr-3"></i>
                  <span>Dubai, VAE</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">
                <a href="#services" className="hover:text-red-500 transition-colors">Services</a>
              </h4>
              <ul className="space-y-2 text-gray-300">
                <li>Firmengründung Dubai</li>
                <li>Investor Visa</li>
                <li>Geschäftskonto</li>
                <li>Büroservice</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 NextStep. Alle Rechte vorbehalten.
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="/impressum" className="text-gray-400 hover:text-red-500 transition-colors">
                Impressum
              </a>
              <a href="/datenschutz" className="text-gray-400 hover:text-red-500 transition-colors">
                Datenschutz
              </a>
              <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-red-500 transition-colors">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky Actions for Mobile */}
      <StickyActions />
    </div>
  );
};

export default Home;
